import axios from 'axios';
import { getToken, getRefreshToken, setToken, setRefreshToken, clearToken } from '../utils/storage';

const api = axios.create({
    baseURL: '',
});

let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error, token = null) => {
    pendingQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    pendingQueue = [];
};

api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (res) => res,
    async (error) => {
        const originalRequest = error.config;
        const status = error?.response?.status;

        if (status !== 401) {
            return Promise.reject(error);
        }

        const refreshToken = getRefreshToken();
        if (!refreshToken) {
            clearToken();
            return Promise.reject(error);
        }

        if (originalRequest._retry) {
            clearToken();
            return Promise.reject(error);
        }
        originalRequest._retry = true;

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingQueue.push({
                    resolve: (token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(api(originalRequest));
                    },
                    reject,
                });
            });
        }

        isRefreshing = true;

        try {
            const res = await api.get('/api/auth/renew-token', {
                headers: { Authorization: `Bearer ${refreshToken}` },
            });
            const newToken = res?.data?.data?.token;
            const newRefresh = res?.data?.data?.refreshToken;
            if (newToken) setToken(newToken);
            if (newRefresh) setRefreshToken(newRefresh);
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
        } catch (err) {
            clearToken();
            processQueue(err, null);
            return Promise.reject(err);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
